import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { logError } from '../lib/logger';
import {
  MediaAttachmentStates,
  enqueueUpload,
  isUploadActiveState,
  removeMediaAttachment,
  removeMediaAttachmentsForSessionKey,
  useMediaAttachment,
} from '../lib/media_attachments_store';

export const backgroundImageVariantUrl = (backgroundImage) => {
  return (
    backgroundImage?.variants?.large?.public_url || backgroundImage?.variants?.medium?.public_url || backgroundImage?.public_url || null
  );
};

/**
 * @typedef {Object} BackgroundImageUploadOptions
 * @property {Record<string, any> | null} [backgroundImage]
 * @property {string | null} [backgroundImageUrl]
 * @property {string | null} [uploadWorkspaceId]
 * @property {string} [sessionKeyScope]
 * @property {string | null} [resetKey]
 * @property {boolean} [externallyDisabled]
 * @property {string[]} [contentTypes]
 * @property {(attachmentId: string | null) => Promise<Record<string, any> | null | undefined>} [onSave]
 */

// Shared controller for the workspace settings panel and the compact conversation
// picker. It owns temporary previews, upload persistence, retry state, and assigning
// the completed attachment through the caller-provided save mutation.
const useBackgroundImageUpload = (
  /** @type {BackgroundImageUploadOptions} */ {
    backgroundImage = null,
    backgroundImageUrl = null,
    uploadWorkspaceId = null,
    sessionKeyScope = 'background-image',
    resetKey = null,
    externallyDisabled = false,
    contentTypes = [],
    onSave,
  },
) => {
  const [currentUploadId, setCurrentUploadId] = useState(null);
  const [savedAttachmentId, setSavedAttachmentId] = useState(null);
  const [failedSaveAttachmentId, setFailedSaveAttachmentId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [localBackgroundImage, setLocalBackgroundImage] = useState(null);
  const [hasLocalOverride, setHasLocalOverride] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const localPreviewUrlRef = useRef(null);
  const mountedRef = useRef(false);

  const currentUpload = useMediaAttachment(currentUploadId);
  const currentAttachmentId = currentUpload?.attachment_id;
  const uploadState = currentUpload?.upload?.state;
  const isUploading = isUploadActiveState(uploadState);
  const isProcessing = uploadState === MediaAttachmentStates.processing;
  const isUploadComplete = uploadState === MediaAttachmentStates.completed;
  const isUploadError = uploadState === MediaAttachmentStates.error;
  const uploadProgress = Math.round(currentUpload?.upload?.progress ?? (isUploadComplete ? 100 : 0));
  const isBusy = isUploading || isProcessing || isSaving;
  const isDisabled = isBusy || externallyDisabled;
  const uploadSessionKey = useMemo(
    () => `${sessionKeyScope}:background-image:${uploadWorkspaceId || 'unknown'}`,
    [sessionKeyScope, uploadWorkspaceId],
  );
  // Ignore async save results after the editor is retargeted to another resource.
  const saveScopeRef = useRef({ onSave, uploadSessionKey });
  saveScopeRef.current = { onSave, uploadSessionKey };
  const displayedBackgroundImage = hasLocalOverride ? localBackgroundImage : backgroundImage;
  const savedBackgroundImageUrl = hasLocalOverride
    ? backgroundImageVariantUrl(localBackgroundImage)
    : backgroundImageVariantUrl(backgroundImage) || backgroundImageUrl || null;
  const displayedImageUrl = localPreviewUrl || savedBackgroundImageUrl;
  const hasSavedBackgroundImage = Boolean(displayedBackgroundImage || savedBackgroundImageUrl);

  const replaceLocalPreviewUrl = useCallback((file) => {
    if (localPreviewUrlRef.current) URL.revokeObjectURL(localPreviewUrlRef.current);

    const nextUrl = file ? URL.createObjectURL(file) : null;
    localPreviewUrlRef.current = nextUrl;
    setLocalPreviewUrl(nextUrl);
  }, []);

  const cleanupUploads = useCallback(() => {
    return removeMediaAttachmentsForSessionKey(uploadSessionKey, { workspaceId: uploadWorkspaceId }).catch((cleanupError) => {
      logError('useBackgroundImageUpload', 'Failed to cleanup uploads', { error: cleanupError });
    });
  }, [uploadSessionKey, uploadWorkspaceId]);

  const cleanupUpload = useCallback((uploadId) => {
    if (!uploadId) return Promise.resolve();

    return removeMediaAttachment(uploadId).catch((cleanupError) => {
      logError('useBackgroundImageUpload', 'Failed to cleanup upload', { error: cleanupError, uploadId });
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setHasLocalOverride(false);
    setLocalBackgroundImage(null);
    setCurrentUploadId(null);
    setSavedAttachmentId(null);
    setFailedSaveAttachmentId(null);
    setIsSaving(false);
    setError(null);
    replaceLocalPreviewUrl(null);
  }, [replaceLocalPreviewUrl, resetKey]);

  useEffect(() => {
    return () => {
      cleanupUploads();
      replaceLocalPreviewUrl(null);
    };
  }, [cleanupUploads, replaceLocalPreviewUrl]);

  const saveBackgroundImage = useCallback(
    async (attachmentId, uploadId = null) => {
      if (!onSave) return;

      const saveScope = { onSave, uploadSessionKey };
      const isCurrentSave = () => {
        return (
          mountedRef.current &&
          saveScopeRef.current.onSave === saveScope.onSave &&
          saveScopeRef.current.uploadSessionKey === saveScope.uploadSessionKey
        );
      };

      setIsSaving(true);
      setError(null);

      try {
        const updatedBackgroundImage = await onSave(attachmentId || null);
        if (!isCurrentSave()) {
          await cleanupUpload(uploadId);
          return;
        }

        if (attachmentId) setSavedAttachmentId(attachmentId);
        setFailedSaveAttachmentId(null);
        setLocalBackgroundImage(updatedBackgroundImage || null);
        setHasLocalOverride(true);
        setCurrentUploadId(null);
        replaceLocalPreviewUrl(null);
        await cleanupUpload(uploadId);
      } catch (saveError) {
        if (!isCurrentSave()) {
          await cleanupUpload(uploadId);
          return;
        }

        if (attachmentId) setFailedSaveAttachmentId(attachmentId);
        setError('Unable to save background image.');
        logError('useBackgroundImageUpload', 'Failed to save background image', { error: saveError });
      } finally {
        if (isCurrentSave()) setIsSaving(false);
      }
    },
    [cleanupUpload, onSave, replaceLocalPreviewUrl, uploadSessionKey],
  );

  useEffect(() => {
    if (
      !isUploadComplete ||
      !currentAttachmentId ||
      currentAttachmentId === savedAttachmentId ||
      currentAttachmentId === failedSaveAttachmentId ||
      isSaving
    ) {
      return;
    }

    saveBackgroundImage(currentAttachmentId, currentUploadId);
  }, [currentAttachmentId, currentUploadId, failedSaveAttachmentId, isUploadComplete, isSaving, saveBackgroundImage, savedAttachmentId]);

  const uploadFile = useCallback(
    async (file) => {
      if (!file || !uploadWorkspaceId) return null;

      if (!contentTypes.includes(file.type)) {
        setError('Unsupported image type.');
        return null;
      }

      setError(null);
      setCurrentUploadId(null);
      setSavedAttachmentId(null);
      setFailedSaveAttachmentId(null);
      replaceLocalPreviewUrl(file);
      await cleanupUploads();

      try {
        const uploadId = await enqueueUpload({
          file,
          purpose: 'workspace_background_image',
          workspaceId: uploadWorkspaceId,
          sessionKey: uploadSessionKey,
        });
        setCurrentUploadId(uploadId);
        return uploadId;
      } catch (uploadError) {
        replaceLocalPreviewUrl(null);
        setError('Unable to start upload.');
        logError('useBackgroundImageUpload', 'Failed to start upload', { error: uploadError });
        return null;
      }
    },
    [cleanupUploads, contentTypes, replaceLocalPreviewUrl, uploadSessionKey, uploadWorkspaceId],
  );

  const rejectFile = useCallback(() => {
    setError('Unsupported image type.');
  }, []);

  const removeBackground = useCallback(() => {
    if (isDisabled || !hasSavedBackgroundImage) return;
    saveBackgroundImage(null);
  }, [hasSavedBackgroundImage, isDisabled, saveBackgroundImage]);

  const canRetrySave = Boolean(currentAttachmentId && currentAttachmentId === failedSaveAttachmentId && !isDisabled);
  const retrySave = useCallback(() => {
    if (!canRetrySave) return;
    saveBackgroundImage(currentAttachmentId, currentUploadId);
  }, [canRetrySave, currentAttachmentId, currentUploadId, saveBackgroundImage]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    canRetrySave,
    clearError,
    currentUpload,
    displayedImageUrl,
    error,
    hasSavedBackgroundImage,
    isBusy,
    isDisabled,
    isProcessing,
    isSaving,
    isUploadError,
    isUploading,
    rejectFile,
    removeBackground,
    retrySave,
    uploadFile,
    uploadProgress,
  };
};

export default useBackgroundImageUpload;
